/**
 * Error monitoring with optional Sentry integration.
 * 
 * If VITE_SENTRY_DSN is set, errors forward to Sentry.
 * Otherwise, structured console.error (dev/staging fallback).
 *
 * ENV VARS NEEDED:
 *   VITE_SENTRY_DSN       – Sentry project DSN (publishable, safe for frontend)
 *   VITE_SENTRY_ENV        – "production" | "staging" | "development" (default: "production")
 *
 * Deduplicates by message+source within a 60s window to avoid spam.
 */

import * as Sentry from '@sentry/react';

const DEDUPE_WINDOW_MS = 60_000;
const MAX_ERRORS_PER_SESSION = 20;
const seenErrors = new Map<string, number>();
let errorCount = 0;
let sentryInitialised = false;

export interface ErrorReport {
  message: string;
  source?: string;
  stack?: string;
  url: string;
  userAgent: string;
  timestamp: string;
  sessionId?: string;
}

function dedupeKey(msg: string, source?: string): string {
  return `${msg}::${source || ''}`;
}

function shouldReport(msg: string, source?: string): boolean {
  if (errorCount >= MAX_ERRORS_PER_SESSION) return false;
  const key = dedupeKey(msg, source);
  const lastSeen = seenErrors.get(key);
  if (lastSeen && Date.now() - lastSeen < DEDUPE_WINDOW_MS) return false;
  seenErrors.set(key, Date.now());
  errorCount++;
  return true;
}

function buildReport(error: Error | string, source?: string): ErrorReport {
  const err = typeof error === 'string' ? new Error(error) : error;
  return {
    message: err.message?.slice(0, 500) || 'Unknown error',
    source: source?.slice(0, 200),
    stack: err.stack?.slice(0, 1000),
    url: window.location.href,
    userAgent: navigator.userAgent.slice(0, 200),
    timestamp: new Date().toISOString(),
  };
}

function sendReport(report: ErrorReport, originalError?: Error): void {
  if (sentryInitialised && originalError) {
    Sentry.captureException(originalError, {
      tags: { source: report.source || 'unknown' },
      extra: { url: report.url, timestamp: report.timestamp },
    });
  } else if (sentryInitialised) {
    Sentry.captureMessage(report.message, {
      level: 'error',
      tags: { source: report.source || 'unknown' },
      extra: { url: report.url, stack: report.stack, timestamp: report.timestamp },
    });
  }
  // Always log structured for local debugging
  console.error('[ErrorMonitor]', JSON.stringify(report));
}

/**
 * Initialise Sentry if DSN is available, then install global handlers.
 * Call once in main.tsx.
 */
let installed = false;

export function installErrorMonitor(): void {
  if (installed) return;
  installed = true;

  const dsn = import.meta.env.VITE_SENTRY_DSN || 'https://fdc44b6a62be034aa504a56532738c5c@o4510953831006208.ingest.de.sentry.io/4510953835855952';
  const environment = import.meta.env.VITE_SENTRY_ENV || 'production';

  if (dsn) {
    try {
      Sentry.init({
        dsn,
        environment,
        release: `ruty@${import.meta.env.VITE_APP_VERSION || '0.0.0'}`,
        sampleRate: 1.0, // 100% until we have baseline
        tracesSampleRate: 0.1, // light performance tracing
        integrations: [
          Sentry.browserTracingIntegration(),
        ],
        // Don't send errors from browser extensions
        beforeSend(event) {
          const frames = event.exception?.values?.[0]?.stacktrace?.frames || [];
          if (frames.some(f => f.filename?.includes('extension://'))) return null;
          return event;
        },
      });
      sentryInitialised = true;
    } catch (e) {
      console.warn('[ErrorMonitor] Sentry init failed:', e);
    }
  }

  window.addEventListener('error', (event) => {
    if (!shouldReport(event.message, event.filename)) return;
    const report = buildReport(event.error || event.message, event.filename);
    sendReport(report, event.error instanceof Error ? event.error : undefined);
  });

  window.addEventListener('unhandledrejection', (event) => {
    const msg = event.reason?.message || String(event.reason);
    if (!shouldReport(msg, 'unhandledrejection')) return;
    const report = buildReport(event.reason || msg, 'unhandledrejection');
    sendReport(report, event.reason instanceof Error ? event.reason : undefined);
  });
}

/** Reset installed state (for testing only) */
export function _resetInstallState(): void {
  installed = false;
}

/**
 * Manual error reporting for caught errors.
 */
export function reportError(error: Error | string, source?: string): void {
  const msg = typeof error === 'string' ? error : error.message;
  if (!shouldReport(msg, source)) return;
  const report = buildReport(error, source);
  sendReport(report, error instanceof Error ? error : undefined);
}

/** Whether Sentry is active (useful for status checks) */
export function isSentryActive(): boolean {
  return sentryInitialised;
}
